/**
 * The envelope's history: what was refilled, what was spent, and what moved
 * to the goal — newest first.
 *
 * A correction is only "visible" if something displays it, and the whole
 * argument for undo writing a reversal instead of deleting is that the family
 * can see what happened. Reversed spends stay on the list, struck through,
 * with the correction it produced hidden underneath (its own `reversal` row
 * is never shown as a line item — the strikethrough on the entry it corrects
 * *is* the display of it, and showing both would say the same thing twice).
 *
 * A rollover pair spans two ledgers (see CLAUDE.md, "A rollover pair spans
 * two ledgers"). This is the envelope's history, so only the half that left
 * the tank belongs here — the half that arrived in the goal is that goal's
 * own history, not this one's, and showing both reads as money bouncing in
 * and out of the same list for no reason.
 *
 * `tiles` is the full set including archived ones, not the set that is still
 * tappable. A tile is archived rather than deleted so that three months of
 * "Ice cream" doesn't silently become three months of "Spend" the afternoon a
 * parent tidies up the logging screen.
 */

import { formatCents } from '../domain/money.js';

export default function History({ entries, tiles, goalName }) {
  const reversed = new Set(entries.filter((e) => e.reversesId).map((e) => e.reversesId));
  const shown = entries
    .filter((e) => e.ledger !== 'goal')
    .filter((e) => e.reasonType !== 'reversal')
    .slice()
    .reverse();
  const tileName = (id) => tiles.find((t) => t.id === id)?.label;

  if (!shown.length) return null;

  return (
    <section className="card stack tight" aria-labelledby="history-heading">
      <p className="eyebrow" id="history-heading">History</p>
      <ul className="stack tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {shown.map((e) => {
          const undone = reversed.has(e.id);
          return (
            <li key={e.id} className="spread small">
              <span className={undone ? 'faint' : ''} style={undone ? { textDecoration: 'line-through' } : undefined}>
                {e.reasonType === 'envelope_refill' ? 'Refill'
                  : e.reasonType === 'envelope_rollover' ? `To ${goalName ?? 'the goal'}`
                  // A one-off logged with no tile carries whatever the parent
                  // typed. "Spend" is the floor, not the first choice: the
                  // note is the only thing that tells last Tuesday's $4 apart
                  // from the other one.
                  : tileName(e.tileId) ?? e.note ?? 'Spend'}
                {undone && <span className="faint"> · undone</span>}
              </span>
              <span className={`money ${undone ? 'faint' : ''}`}>
                {formatCents(e.amountCents, { sign: true })}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="small faint">{shown.length} logged · corrections stay on the list</p>
    </section>
  );
}
